import PublicNav from "@/components/PublicNav";
import Footer, { startLogin } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MapPin, Ruler, TrendingDown, Clock, ArrowRight, ArrowLeftRight, Sparkles } from "lucide-react";

export const STORIES = [
  {
    id: "noida-aac-4500",
    title: "How a Noida family saved ₹6.8L switching red brick to AAC blocks",
    city: "Noida, Sector 128",
    area: "4,500 sq ft villa",
    image: "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbmRpYW4lMjBob3VzZSUyMGNvbnN0cnVjdGlvbiUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTk1MzA5M3ww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "Red clay brick", to: "AAC blocks" }],
    saving: "₹6.8 Lakh", pct: "11%", time: "3 weeks faster",
    summary: "Nirmantra's report flagged that AAC blocks would cut wall weight, mortar and even structural steel for this 4,500 sq ft villa.",
    body: [
      "When the Sharmas began their 4,500 sq ft villa in Noida, their contractor had quoted conventional red clay brickwork. Nirmantra's AI Construction Health Report analysed the BOQ and flagged that switching to AAC (Autoclaved Aerated Concrete) blocks would deliver savings on three fronts at once.",
      "AAC blocks are larger and lighter, so masonry went up faster with far less mortar and plaster. Because the walls weighed less, the structural engineer was able to marginally optimise beam and slab steel. The lighter dead load also suited the soil conditions revealed in the soil report.",
      "The net result: about ₹6.8 lakh saved — roughly 11% of the shell cost — and nearly three weeks shaved off the masonry schedule. As a bonus, AAC's thermal insulation reduced west-facing heat gain, which the family now feels in lower cooling bills.",
    ],
  },
  {
    id: "gurgaon-hdhmr-6000",
    title: "Gurgaon homeowner cut ₹4.2L on joinery with HDHMR over BWP ply",
    city: "Gurgaon, DLF Phase 3",
    area: "6,000 sq ft residence",
    image: "https://images.unsplash.com/photo-1723110994499-df46435aa4b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGZhY2FkZSUyMGluZGlhfGVufDB8fHx8MTc4NTk1MzEwNXww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "BWP plywood", to: "HDHMR boards" }],
    saving: "₹4.2 Lakh", pct: "9%", time: "Better moisture life",
    summary: "For all wardrobes, kitchen and TV units, HDHMR matched BWP ply's performance at a lower rate — with a smoother, screw-holding, termite-resistant core.",
    body: [
      "This 6,000 sq ft Gurgaon home had extensive internal joinery — wardrobes in five bedrooms, a large modular kitchen, and multiple storage units. The interior contractor had specified BWP (Boiling Water Proof) plywood throughout.",
      "Nirmantra's advisor compared the quotation against HDHMR (High Density High Moisture Resistance) boards. For dry and semi-wet internal applications, HDHMR offered comparable durability with a denser, uniform core that holds screws well, resists termites, and gives a flawless finish for laminates and veneers — at a lower per-sheet cost.",
      "Keeping BWP ply only where it truly mattered (under-sink kitchen bases) and moving the rest to HDHMR saved about ₹4.2 lakh — around 9% of the joinery budget — without compromising the premium look the owner wanted.",
    ],
  },
  {
    id: "delhi-gypsum-3200",
    title: "A Delhi home saved ₹5.5L and 4 weeks with smarter finishes",
    city: "Delhi, Vasant Kunj",
    area: "3,200 sq ft home",
    image: "https://images.unsplash.com/photo-1686164748506-4311ba437c24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBpbmRpYW4lMjBob3VzZSUyMGNvbnN0cnVjdGlvbiUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTk1MzA5M3ww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "Sand-cement plaster", to: "Gypsum plaster" }, { from: "Imported marble", to: "Double-charge vitrified" }],
    saving: "₹5.5 Lakh", pct: "12%", time: "4 weeks faster",
    summary: "Gypsum plaster removed the long curing wait, and double-charge vitrified tiles gave a marble-like look at a fraction of the cost.",
    body: [
      "For this 3,200 sq ft Vasant Kunj home, the owner wanted a premium finish but a controlled budget. Two recommendations from Nirmantra's report made the biggest difference.",
      "First, internal walls moved from traditional sand-cement plaster to machine-applied gypsum plaster. It needs no water curing, gives a ready-to-paint smooth surface, and cut nearly four weeks of curing and levelling time — accelerating the whole finishing phase.",
      "Second, instead of imported marble in living and bedroom areas, the family chose large-format double-charge vitrified tiles that convincingly mimic marble, cost far less, and are far easier to maintain. Together the two swaps saved about ₹5.5 lakh — close to 12% of the finishing cost.",
    ],
  },
  {
    id: "ghaziabad-upvc-3000",
    title: "Ghaziabad build trimmed ₹3.1L with AAC, uPVC and factory railings",
    city: "Ghaziabad, Raj Nagar Extension",
    area: "3,000 sq ft house",
    image: "https://images.unsplash.com/photo-1626249893783-cc4a9f66880a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGZhY2FkZSUyMGluZGlhfGVufDB8fHx8MTc4NTk1MzEwNXww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "Red brick", to: "AAC blocks" }, { from: "Aluminium windows", to: "uPVC windows" }],
    saving: "₹3.1 Lakh", pct: "8%", time: "Lower upkeep",
    summary: "On a tighter 3,000 sq ft budget, three targeted material swaps lowered both build cost and long-term maintenance.",
    body: [
      "First-time builders in Raj Nagar Extension were anxious about overspending on their 3,000 sq ft home. Nirmantra's report focused on high-impact, low-risk swaps.",
      "Walls moved from red brick to AAC blocks for faster, lighter, better-insulated construction. Windows shifted from powder-coated aluminium to uPVC — cheaper here, and far better at sealing out Delhi-NCR dust and noise while resisting corrosion. Staircase and balcony railings were sourced as factory-finished MS sections instead of costly on-site fabrication.",
      "The combined effect was about ₹3.1 lakh saved — around 8% — plus meaningfully lower maintenance for years to come, which matters most on a first home.",
    ],
  },
  {
    id: "gurgaon-luxury-9000",
    title: "A 9,000 sq ft Gurgaon luxury home saved ₹18L through value engineering",
    city: "Gurgaon, Sohna Road",
    area: "9,000 sq ft luxury residence",
    image: "https://images.unsplash.com/photo-1708430785379-07e00f261b35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGZhY2FkZSUyMGluZGlhfGVufDB8fHx8MTc4NTk1MzEwNXww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "Natural granite", to: "Engineered quartz" }, { from: "Solid teak", to: "HDHMR + veneer" }],
    saving: "₹18 Lakh", pct: "9%", time: "On schedule",
    summary: "Even on a premium project, objective quotation comparison and selective material swaps unlocked large savings without dropping the luxury feel.",
    body: [
      "Luxury does not mean overpaying. For this 9,000 sq ft residence on Sohna Road, Nirmantra reviewed a very detailed BOQ and multiple vendor quotations side by side.",
      "The advisor identified inflated line items and recommended engineered quartz for heavy-use countertops (harder and more stain-resistant than granite in kitchens), and HDHMR with premium veneer in place of solid teak for wardrobes and panelling — visually identical, dimensionally more stable, and far less expensive. The RCC design was also value-engineered after the soil report allowed a leaner, optimised footing layout.",
      "These changes preserved the home's high-end character while saving roughly ₹18 lakh — about 9% of the project — and kept the build on schedule.",
    ],
  },
  {
    id: "noida-readymix-5200",
    title: "Noida owner saved ₹7.5L and 5 weeks with AAC, HDHMR and ready-mix",
    city: "Noida, Sector 150",
    area: "5,200 sq ft home",
    image: "https://images.unsplash.com/photo-1686164748261-33e13eef70b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbmRpYW4lMjBob3VzZSUyMGNvbnN0cnVjdGlvbiUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTk1MzA5M3ww&ixlib=rb-4.1.0&q=85",
    swaps: [{ from: "Red brick", to: "AAC + fly-ash bricks" }, { from: "Site-mixed concrete", to: "Ready-mix concrete" }],
    saving: "₹7.5 Lakh", pct: "10%", time: "5 weeks faster",
    summary: "Combining alternate masonry with ready-mix concrete cut both material wastage and labour time on this 5,200 sq ft home.",
    body: [
      "This Sector 150 home was going up during peak summer, and the owner worried about wastage and delays. Nirmantra's report recommended a package of changes.",
      "Masonry used AAC blocks above the plinth and fly-ash bricks where economical, reducing weight and cost. Internal joinery moved from BWP ply to HDHMR. Crucially, structural concrete switched from labour-intensive site mixing to ready-mix concrete (RMC), which improved quality consistency, slashed cement and aggregate wastage, and sped up slab pours.",
      "Altogether the owner saved about ₹7.5 lakh — roughly 10% — and finished nearly five weeks ahead of the original plan, with better-documented concrete quality thanks to RMC batch records.",
    ],
  },
];

function StoryStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#0E9AC8] shrink-0" strokeWidth={1.75} />
      <span className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{value}</span> {label}</span>
    </div>
  );
}

export default function Blog() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <section className="hero-wash">
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-10 lg:pt-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 mb-6 shadow-sm">
            <Sparkles className="h-4 w-4 text-[#0E9AC8]" strokeWidth={2} />
            <span className="text-xs font-semibold tracking-wide text-slate-600">Real savings, real homeowners</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-extrabold text-slate-900 leading-[1.05]">
            Delhi-NCR homeowners who<br /><span className="text-[#0E9AC8]">saved lakhs with smarter choices.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            AI-powered analysis plus independent expert advice helped these families in Delhi, Noida, Gurgaon and Ghaziabad
            save 8%–12% on projects from 3,000 to 9,000 sq ft — through alternate materials and value engineering.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14 space-y-16">
        {STORIES.map((s, i) => (
          <article key={s.id} data-testid={`blog-story-${s.id}`}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
            <div className="rounded-3xl overflow-hidden card-premium [direction:ltr]">
              <img src={s.image} alt={s.title} className="w-full h-[280px] lg:h-[340px] object-cover" />
            </div>
            <div className="[direction:ltr]">
              <div className="flex flex-wrap gap-4 mb-4">
                <StoryStat icon={MapPin} label="" value={s.city} />
                <StoryStat icon={Ruler} label="" value={s.area} />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">{s.title}</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.swaps.map((sw, j) => (
                  <span key={j} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {sw.from} <ArrowLeftRight className="h-3 w-3 text-[#0E9AC8]" /> {sw.to}
                  </span>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                <StoryStat icon={TrendingDown} label={`saved (${s.pct})`} value={s.saving} />
                <StoryStat icon={Clock} label="" value={s.time} />
              </div>
              <div className="mt-4 space-y-3">
                {s.body.map((p, k) => <p key={k} className="text-sm text-slate-600 leading-relaxed">{p}</p>)}
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="card-premium rounded-3xl p-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-extrabold text-slate-900">Your project could save 5%–15% too.</h2>
          <p className="mt-3 text-slate-600">Get your free AI Construction Health Report and see where your money can work harder.</p>
          <Button onClick={startLogin} data-testid="blog-cta" className="mt-6 bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-12 px-7 transition-colors">
            Get My Construction Health Report <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
